<template>
  <div class="plan-card">
    <div
      v-if="item.popular"
      class="plan-card--popular-mask gradient-animation"
    >
      {{ item.popularText }}
    </div>
    <div class="plan-card__content plan-card__content-top">
      <div class="plan-card__info">
        <div>
          <div class="plan-card__title">
            {{ item.title }}
          </div>
          <div class="plan-card__sub-title">
            {{ item.subTitle }}
          </div>
        </div>
        <div class="pricing__wrapper">
          <div class="pricing-text">
            <span
              class="pricing-text__highlighted"
            >{{
                findFiatByKey(item.prices?.currency)?.symbol ||
                paymentCurrency.symbol ||
                ''
            }}{{
              activeCycle.value == 'annual'
                ? item.prices?.annualPerMonth
                : item.prices?.monthly
            }}
            </span>
            / month
          </div>
          <div class="pricing-text">
            {{
              activeCycle.value == 'annual'
                ? `Billed ${
                    findFiatByKey(item.prices?.currency)?.symbol ||
                    paymentCurrency.symbol ||
                    ''
                }${item.prices?.annual} annually`
                : 'Billed monthly'
            }}
          </div>
        </div>
      </div>
      <div class="plan-card__actions">
        <GlButton
          :brand="!item.features?.trialAvailable"
          class="button__buy gradient-animation"
          full
          :gradient="item.features?.trialAvailable"
          size="large"
          :title="
            item.features?.trialAvailable ? 'Start 7-day trial' : 'Get started '
          "
          @click="onBuyClick(item)"
        />
      </div>
    </div>

    <div class="plan-card__content plan-card__content-bottom">
      <div class="plan-card__info">
        <div
          v-if="item.quota?.length"
          class="plan-card__includes"
        >
          <div class="plan-card__includes-item-wrapper">
            <div
              v-for="q in item.quota"
              :key="q.tier"
              class="plan-card__includes-item"
            >
              <gl-icon
                v-if="q.icon"
                :gray="q.iconType == 'gray'"
                :height="20"
                :name="q.icon"
                :warning="q.iconType == 'warning'"
                :width="20"
              />
              <span v-html="q.label" />
            </div>
          </div>
        </div>
        <div
          v-if="item.includesPackages.length"
          class="plan-card__includes"
        >
          <div class="plan-card__includes-label">
            {{ item.includesPackagesLabel }}
          </div>
          <div class="plan-card__includes-item-wrapper">
            <div
              v-for="p in item.includesPackages"
              :key="p.tier"
              class="plan-card__includes-item"
            >
              <gl-icon
                v-if="p.icon"
                :gray="p.iconType == 'gray'"
                :height="20"
                :name="p.icon"
                :width="20"
              />
              <span>{{ p.label }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="plan-card__actions">
        <div
          class="button__example"
          @click="onExampleClick(item.type)"
        >
          <gl-icon
            :height="24"
            name="example-arrow"
            :width="24"
          />
          <transition name="fade">
            <span class="button__example-text">
              {{ item.title }} Report Example
            </span>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import GlIcon from '@/components/gl-icon'
import GlButton from '@/components/gl-button'
// utils
import { openExample } from '@/utils/go-to-route'
import { findFiatByKey } from '@/utils/format-by-price'
// vuex
import { mapGetters } from 'vuex'

export default {
  components: { GlIcon, GlButton },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    activeCycle: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters('report', ['getCurrentPrices', 'getPurchasedInfo']),
    ...mapGetters('user', ['paymentCurrency']),
  },
  methods: {
    openExample,
    findFiatByKey,
    onBuyClick(item) {
      this.$emit('onBuyClick', {
        planTier: item.tier,
        billingCycle: this.activeCycle.value,
        isTrialRequest: item.features.trialAvailable,
      })
    },
    onExampleClick(value) {
      this.$emit('onExampleClick', value)
    },
  },
}
</script>

<style scoped>
.plan-card {
  position: relative;
  display: grid;
  grid-template-rows: 1fr 1fr;
  width: 367px;
  border-radius: 20px;
  border: 1px solid var(--Basic-300, rgba(9, 21, 64, 0.16));
  background: #fff;
  transition: all 0.5s ease;
}

.plan-card--popular-mask {
  position: absolute;
  inset: -8px;
  top: -36px;
  z-index: 0;
  border-radius: 24px;
  color: var(--Basic-A-900, #fff);
  text-align: center;
  font-size: var(--Size-M, 14px);
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
  padding-top: 8px;
  background: var(--Accent-Main);
  background-size: 150% 150%;
}

.plan-card:hover {
  scale: 1.02;
  transition: all 0.5s ease;
}

.plan-card__content {
  z-index: 1;
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  justify-content: space-between;
  background: #fff;
}

.plan-card__content-top {
  border-radius: 20px 20px 0 0;
}
.plan-card__content-bottom {
  border-top: 1px solid var(--Basic-300, rgba(9, 21, 64, 0.16));
  padding: 20px 24px 24px 24px;
  border-radius: 0 0 20px 20px;
}
.plan-card__info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.plan-card__title {
  font-size: var(--Size-3XL, 24px);
  font-weight: 700;
  line-height: var(--Line-Height-3XL, 32px);
}
.plan-card__sub-title {
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.plan-card__includes {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plan-card__includes-label {
  font-size: var(--Size-M, 14px);
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);
}

.plan-card__includes-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plan-card__includes-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.plan-card__includes-item::v-deep .value-highlight {
  font-weight: 600;
}

.plan-card__actions {
  width: 100%;
}

.button__buy {
  font-weight: 600;
}

.button__example {
  width: 100%;
  display: flex;
  height: 40px;
  padding: 0 12px 0 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Basic-200, rgba(9, 21, 64, 0.08));
  cursor: pointer;
  user-select: none;
}
.button__example-text {
  color: var(--Basic-900, #091540);
  text-align: center;
  font-size: var(--Size-M, 14px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.pricing__wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pricing-text {
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
  font-size: var(--Size-S, 12px);
  font-weight: 500;
  line-height: var(--Line-Height-S, 16px);
}
.pricing-text__highlighted {
  color: var(--Basic-900, #091540);
  font-size: var(--Size-4XL, 36px);
  font-weight: 600;
  line-height: var(--Line-Height-4XL, 44px);
  letter-spacing: var(--Letter-Spacing-XS, -0.5px);
}
</style>
